/**
 * v3.bookings — WAVE-9 Microsoft Bookings client.
 *
 * Backed by Functions/BookingsFunction.cs. Includes Teams Virtual
 * Appointments because Bookings owns the meeting URL provisioning —
 * v3 only surfaces the joinUrl from each appointment row.
 */

import type {
  RequestOptions,
  BookingsCalendar,
  BookingsService as BookingsServiceDto,
  BookingsStaff,
  BookingsAppointment,
  BookingsCancelInput,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

export interface BookingsCalendarsResponse {
  success: boolean;
  calendars: ReadonlyArray<BookingsCalendar>;
}

export interface BookingsServicesResponse {
  success: boolean;
  services: ReadonlyArray<BookingsServiceDto>;
  error?: string;
}

export interface BookingsStaffResponse {
  success: boolean;
  staff: ReadonlyArray<BookingsStaff>;
  error?: string;
}

export interface BookingsAppointmentsResponse {
  success: boolean;
  count: number;
  appointments: ReadonlyArray<BookingsAppointment>;
  error?: string;
}

export interface BookingsActionResponse {
  success: boolean;
  ticketId?: string;
  ticketNumber?: string;
  branchCode?: string;
  error?: string;
}

export class BookingsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public calendars(opts?: RequestOptions): Promise<BookingsCalendarsResponse> {
    return this.http.get<BookingsCalendarsResponse>('/v3/bookings/calendars', { ...opts });
  }

  public services(calendarId: string, opts?: RequestOptions): Promise<BookingsServicesResponse> {
    return this.http.get<BookingsServicesResponse>('/v3/bookings/services', {
      ...opts,
      query: { calendarId },
    });
  }

  public staff(calendarId: string, opts?: RequestOptions): Promise<BookingsStaffResponse> {
    return this.http.get<BookingsStaffResponse>('/v3/bookings/staff', {
      ...opts,
      query: { calendarId },
    });
  }

  public appointments(
    filters: { calendarId: string; date?: string; to?: string },
    opts?: RequestOptions,
  ): Promise<BookingsAppointmentsResponse> {
    return this.http.get<BookingsAppointmentsResponse>('/v3/bookings/appointments', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public cancel(
    appointmentCacheId: string,
    body: BookingsCancelInput,
    opts?: RequestOptions,
  ): Promise<BookingsActionResponse> {
    return this.http.post<BookingsActionResponse>(
      `/v3/bookings/appointments/${encodeURIComponent(appointmentCacheId)}/cancel`,
      { ...opts, body },
    );
  }

  public checkIn(
    appointmentCacheId: string,
    opts?: RequestOptions,
  ): Promise<BookingsActionResponse> {
    return this.http.post<BookingsActionResponse>(
      `/v3/bookings/appointments/${encodeURIComponent(appointmentCacheId)}/check-in`,
      { ...opts, body: {} },
    );
  }
}
